import { PurchaseTypeEnum } from 'api-hooks/order/common';
import { DocumentPurchaseModel } from 'api-hooks/order/travel-document';
import {
  DetailModel,
  TravelDocumentSalesModel,
} from 'api-hooks/sale/travel-document';
import { generateId } from 'common/utils/string';
import { Input } from 'components/elements';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import AgentSelectInput from 'legacy-modules/master/agent/components/select-input';
import PaymentChannelSelectInput from 'legacy-modules/master/payment-channel/components/select-input';
import AgentSupendToast from 'legacy-modules/order/component/agent-suspend-toast';
import TransactionAt from 'legacy-modules/sales/component/transaction-at';
import { guestNameTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import TravelDocumentInvoiceSelectInput from 'legacy-modules/sales/travel-document/components/invoice-select-input';
import {
  CardSection,
  FullContainer,
  HalfContainer,
  SectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import TourListSelectInput from 'legacy-modules/tour/tour-list/components/select-input';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  DocumentPurchaseFormType,
  DocumentPurchaseItemFormType,
  DocumentPurchaseItemGuestDetailFormType,
} from './form-type';
import { addNewDocumentPurchase } from '../hooks/use-get-default-values';

interface Props {
  travelDocument?: DocumentPurchaseModel;
  defaultValues?: DocumentPurchaseFormType;
}

export default function PurchaseDetailField(props: Props) {
  const { t } = useTranslation();

  const { setValue, control, getValues, reset } = useFormContext();
  const { editable } = useFormState();
  const [type, documentSale, orderNumber, total, remainingTransactionQuota] =
    useWatch({
      control,
      name: [
        'type',
        'documentSale',
        'orderNumber',
        'total',
        'remainingTransactionQuota',
      ],
    });

  const isExceedingQuota =
    remainingTransactionQuota && total > remainingTransactionQuota;

  React.useEffect(() => {
    setValue('isExceedingQuota', isExceedingQuota || false);
  }, [setValue, isExceedingQuota]);

  const invoiceId = getValues('invoiceId');
  React.useEffect(() => {
    if ((!invoiceId || invoiceId === '') && !props.defaultValues) {
      reset();
    }
  }, [invoiceId, props.defaultValues, reset]);

  const onClickInvoiceNumber = React.useCallback(
    (data: TravelDocumentSalesModel) => {
      if (editable) {
        // const visaDocumentOptions =
        //   data?.documentSales
        //     ?.map((ds) => {
        //       return (ds.visaDocuments || [])
        //         .map((vd) => {
        //           return {
        //             id: vd.id,
        //             label: vd.name,
        //             visaDocumentId: vd.id,
        //             price: vd.price || 0,
        //             preferenceId: vd.preference.id,
        //           };
        //           // MULTIPRICE
        //           // return (vd.visaDocumentItems || []).map((vdi) => {
        //           //   const option = {
        //           //     id: vdi.preference.id,
        //           //     label: [vd.code, vd.name, vdi.preference.name].join(
        //           //       ' - ',
        //           //     ),
        //           //     preferenceId: vdi.preference.id,
        //           //     price: vdi.price,
        //           //     visaDocumentItemId: vdi.id,
        //           //   };
        //           //   return option;
        //           // });
        //         })
        //         .flat();
        //     })
        //     .flat() ?? [];

        const documentPurchases: DocumentPurchaseItemFormType[] =
          data.documentSales.map((ds) => {
            const documentPurchaseAddOns: DocumentPurchaseItemFormType['documentPurchaseAddOns'] =
              ds.documentSaleAddOns.map((dsao) => {
                const guest = dsao.guest;
                return {
                  description: dsao.description || '',
                  guestId: guest.id,
                  guestName: guestNameTransformer(guest),
                  price: dsao.price,
                  customId: generateId(),
                  id: undefined,
                };
              });

            const documentPurchaseGuests: DocumentPurchaseItemFormType['documentPurchaseGuests'] =
              ds.documentSaleGuests.map((dsg) => {
                const guest = dsg.guest;

                const details: DocumentPurchaseItemGuestDetailFormType[] =
                  (() => {
                    const detailGenerator = (item: DetailModel) => {
                      const visaDocument = item.visaDocument;
                      return {
                        appointmentAt: item.appointmentAt || new Date(),
                        biometric: item.biometric || false,
                        company: item.company || '',
                        coverArea: item.coverArea || '',
                        days: item.days || 1,
                        description: item.description || '',
                        endedAt: item.endAt || new Date(),
                        package: item.package || '',
                        price: item.price,
                        startedAt: item.startAt || new Date(),
                        type: item.type || '',
                        customId: generateId(),
                        id: undefined,
                        label: visaDocument?.name || '',
                        visaDocumentId: visaDocument?.id || '',
                      } as DocumentPurchaseItemGuestDetailFormType;
                    };
                    if (Array.isArray(dsg.details)) {
                      return dsg.details.map((detail) => {
                        return detailGenerator(detail);
                      });
                    } else {
                      return [detailGenerator(dsg.details)];
                    }
                  })();

                return {
                  guestId: guest.id,
                  guestName: guestNameTransformer(guest),
                  details,
                  customId: generateId(),
                  id: undefined,
                  //additional information
                  company: details[0].company,
                  coverArea: details[0].coverArea,
                  days: details[0].days,
                  description: details[0].description,
                  endedAt: details[0].endedAt,
                  package: details[0].package,
                  price: details[0].price,
                  startedAt: details[0].startedAt,
                  type: details[0].type,
                };
              });
            return {
              customId: generateId(),
              id: undefined,
              documentPurchaseAddOns,
              documentPurchaseGuests,
              documentType: ds.documentType,
              visaDocuments: (ds.visaDocuments || []).map((vd) => {
                return {
                  id: vd.id,
                  label: [vd.code, vd.name].join(' - '),
                  extra: vd,
                };
              }),
            };
          });

        setValue('documentPurchases', documentPurchases, {
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue('discount', data.discount, {
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue('otherCost', data.otherCost, {
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue('saleTotal', data.total, {
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue('invoiceId', data.id, {
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue('invoiceRefId', data.refId, {
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    },
    [editable, setValue],
  );

  const onEnumAfterChange = React.useCallback(() => {
    // @ts-ignore
    setValue('invoiceId', '', {
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue('tourId', '', {
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue('invoiceRefId', '', {
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue('tourRefId', '', {
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [setValue]);

  const agentId = getValues('agentId');
  React.useEffect(() => {
    if (!agentId || agentId === '') {
      setValue('agentStatus', '');
      setValue('remainingTransactionQuota', null);
      setValue('isExceedingQuota', false);
    }
  }, [agentId, setValue]);

  const onAfterAgentDetailChange = React.useCallback(
    (result) => {
      if (editable) {
        const remainingTransactionQuota =
          result.system.remainingTransactionQuota;
        const isExceedingQuota =
          result.system.isTransactionLimited &&
          total > remainingTransactionQuota;

        setValue('isExceedingQuota', isExceedingQuota);
        setValue(
          'remainingTransactionQuota',
          remainingTransactionQuota || null,
        );
        setValue('agentStatus', result.status);
      }
    },
    [editable, setValue, total],
  );

  const disabled = !!props.travelDocument || !!documentSale;

  return (
    <CardSection>
      <FullContainer>
        <Text variant="HeadingXSmall">{t('common:purchase_details')}</Text>
        <SeparatorVertical $margin={24} />
        <SectionContainer>
          <HalfContainer
            $style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }}
          >
            <TransactionAt
              name="transactionAt"
              clearable={false}
              hasTime
              required
            />
          </HalfContainer>
          {!!orderNumber && (
            <HalfContainer>
              <Input
                type="text"
                name="orderNumber"
                label={t('common:order_number')}
                placeholder={t('common:order_number')}
                disabled
              />
            </HalfContainer>
          )}
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="select"
              label={t('common:type')}
              placeholder={t('common:type')}
              name="type"
              required
              onAfterChange={onEnumAfterChange}
              clearable={false}
              disabled={disabled}
              options={[
                { id: PurchaseTypeEnum.Sales, label: t('common:sales') },
                { id: PurchaseTypeEnum.Tour, label: t('common:tour') },
              ]}
            />
          </HalfContainer>
          <HalfContainer>
            {type === PurchaseTypeEnum.Sales ? (
              <TravelDocumentInvoiceSelectInput
                label={t('common:invoice_number')}
                placeholder={t('common:choose_invoice')}
                name="invoiceId"
                onAfterDetailChange={onClickInvoiceNumber}
                required
                disabled={disabled}
              />
            ) : (
              type === PurchaseTypeEnum.Tour && (
                <TourListSelectInput
                  name="tourId"
                  disabled={!!props.travelDocument}
                  required
                  onAfterDetailChange={(value) => {
                    setValue('tourRefId', value.refId);
                    const documentPurchases = getValues('documentPurchases');
                    if (!documentPurchases || documentPurchases.length === 0) {
                      setValue(
                        'documentPurchases',
                        [addNewDocumentPurchase()],
                        {
                          shouldValidate: true,
                          shouldTouch: true,
                        },
                      );
                    }
                  }}
                />
              )
            )}
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <AgentSelectInput
              name="agentId"
              remainingTransactionQuota={remainingTransactionQuota}
              onAfterDetailChange={onAfterAgentDetailChange}
              required
            />
          </HalfContainer>
          <HalfContainer>
            <PaymentChannelSelectInput name="paymentChannelId" />
          </HalfContainer>
        </SectionContainer>
      </FullContainer>
      <AgentSupendToast />
    </CardSection>
  );
}
